import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, Compass, Sparkles, Quote } from "lucide-react";
import { TIERS } from "@/lib/tiers";
import TierCard from "@/components/TierCard";
import MeetSasha from "@/components/MeetSasha";

const HERO_IMG =
  "https://images.unsplash.com/photo-1705326701287-346fc37a2c86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbHV4dXJ5JTIwbW9kZXJuJTIwbGl2aW5nJTIwcm9vbSUyMGludGVyaW9yfGVufDB8fHx8MTc4MjI4ODMzNXww&ixlib=rb-4.1.0&q=85";
const APPROACH_IMG =
  "https://images.unsplash.com/photo-1759038085950-1234ca8f5fed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwaG90ZWwlMjBjb25jaWVyZ2UlMjBkZXNrJTIwaG9zcGl0YWxpdHl8ZW58MHx8fHwxNzgyMjg4MzM1fDA&ixlib=rb-4.1.0&q=85";

const APPROACH_STEPS = [
  {
    icon: Compass,
    title: "We listen first",
    body: "A conversation about your home, your guests and what you'd quietly like to step away from.",
  },
  {
    icon: Leaf,
    title: "We tailor a plan",
    body: "Choose a service level, then we craft the details — pricing, presentation, hospitality — to fit.",
  },
  {
    icon: Sparkles,
    title: "We host, you breathe",
    body: "From the first inquiry to the last linen change, we look after every detail with quiet care.",
  },
];

const STORIES = [
  {
    quote:
      "Sasha turned a small coastal apartment into something I'm genuinely proud of. Bookings doubled in a season — and I barely lift a finger.",
    author: "Inês M.",
    role: "Host, Cascais",
  },
  {
    quote:
      "What I love most is the attention. They know my guests' names, remember preferences, and treat the house like their own.",
    author: "Daniel R.",
    role: "Host, Comporta",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Landing() {
  return (
    <main data-testid="landing-page" className="bg-[#FAF9F6]">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 lg:pt-24 pb-20 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="eyebrow" data-testid="hero-eyebrow">
              A hosting partner · since 2014
            </div>
            <h1
              data-testid="hero-headline"
              className="font-serif-display mt-6 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#2A1F1A]"
            >
              Hosting
              <br />
              <span className="italic text-[#C03028]">elevated.</span>
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-8 text-base lg:text-lg leading-relaxed text-[#6E6E70] max-w-xl"
            >
              Sasha is a considered service for owners who want their homes
              hosted beautifully — with the warmth of a private studio and the
              precision of a hotel team.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/onboard"
                data-testid="hero-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm tracking-wide rounded-none"
              >
                Choose your hosting option
                <ArrowRight size={16} />
              </Link>
              <a
                href="#service-levels"
                data-testid="hero-cta-secondary"
                className="btn-secondary inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm tracking-wide rounded-none"
              >
                Compare levels
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              {[
                ["240+", "homes hosted"],
                ["96%", "occupancy avg."],
                ["4.94", "guest rating"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="font-serif-display text-3xl text-[#2A1F1A]">
                    {k}
                  </div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#6E6E70] mt-1">
                    {v}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative grain-overlay overflow-hidden">
              <img
                src={HERO_IMG}
                alt="A quiet, light-filled living room"
                className="w-full h-[520px] lg:h-[640px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#FAF9F6] border border-[#E3DED5] px-6 py-4 max-w-xs">
              <div className="eyebrow">No. 037</div>
              <div className="font-serif-display text-lg text-[#2A1F1A] mt-1">
                Casa da Lua, Comporta
              </div>
              <div className="text-xs text-[#6E6E70] mt-1">
                Hosted by Sasha since 2024
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HOST GREETING */}
      <section
        data-testid="host-greeting"
        className="bg-[#F2EFE9] border-y border-[#E3DED5]"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 lg:py-20 text-center">
          <motion.div {...fadeUp}>
            <div className="eyebrow">A greeting to our hosts</div>
            <p
              data-testid="host-greeting-text"
              className="font-serif-display mt-6 text-2xl lg:text-3xl leading-snug text-[#2A1F1A]"
            >
              Janté Host&apos;s Concierges is here to assist accommodations
              hosts in getting the most out of their properties.
            </p>
            <p className="mt-5 text-base lg:text-lg text-[#6E6E70]">
              Are you ready to apply for your property to join the Janté
              Hosting Family?
            </p>
            <Link
              to="/onboard"
              data-testid="host-greeting-apply-btn"
              className="btn-primary mt-8 inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm tracking-wide rounded-none"
            >
              Apply to join the family
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MEET SASHA */}
      <MeetSasha />

      {/* APPROACH */}
      <section id="approach" className="bg-[#F2EFE9] border-y border-[#E3DED5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="eyebrow">Janté Way</div>
            <h2 className="font-serif-display mt-5 text-4xl lg:text-5xl text-[#2A1F1A] leading-tight">
              Less management.
              <br />
              <span className="italic">More hospitality.</span>
            </h2>
            <p className="mt-6 text-[#6E6E70] leading-relaxed max-w-md">
              We believe great hosting is felt, not seen. Our work is to remove
              the friction — so what your guests remember is the warmth.
            </p>
          </motion.div>

          <div className="lg:col-span-7 space-y-10">
            {APPROACH_STEPS.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  {...fadeUp}
                  transition={{
                    ...fadeUp.transition,
                    delay: idx * 0.1,
                  }}
                  data-testid={`approach-step-${idx}`}
                  className="flex gap-6 items-start border-t border-[#E3DED5] pt-8 first:border-t-0 first:pt-0"
                >
                  <div className="shrink-0 w-12 h-12 border border-[#C03028]/30 flex items-center justify-center">
                    <Icon className="text-[#C03028]" size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="eyebrow">{`Step 0${idx + 1}`}</div>
                    <h3 className="font-serif-display text-2xl mt-1 text-[#2A1F1A]">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[#6E6E70] leading-relaxed max-w-lg">
                      {step.body}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE LEVELS */}
      <section id="service-levels" className="bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28">
          <motion.div {...fadeUp} className="max-w-2xl">
            <div className="eyebrow">Hosting Options</div>
            <h2 className="font-serif-display mt-5 text-4xl lg:text-5xl text-[#2A1F1A] leading-tight">
              Three ways to host with us.
            </h2>
            <p className="mt-5 text-[#6E6E70] leading-relaxed">
              Choose the level of partnership that fits your home and the time
              you&apos;d like to spend on it. You can change levels at any time.
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {TIERS.map((tier, idx) => (
              <motion.div
                key={tier.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: idx * 0.08 }}
                className={tier.highlighted ? "lg:-mt-4" : ""}
              >
                <DisplayTierLink tier={tier} />
              </motion.div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/onboard"
              data-testid="service-levels-cta"
              className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-sm tracking-wide rounded-none"
            >
              Begin your hosting journey
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* STORIES */}
      <section id="stories" className="bg-[#2A1F1A] text-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="eyebrow" style={{ color: "#B0895C" }}>
              Host stories
            </div>
            <h2 className="font-serif-display mt-5 text-4xl lg:text-5xl leading-tight text-[#FAF9F6]">
              Words from the hosts we keep.
            </h2>
            <div className="mt-8 grain-overlay">
              <img
                src={APPROACH_IMG}
                alt="A quiet concierge desk"
                className="w-full h-72 object-cover"
              />
            </div>
          </motion.div>

          <div className="lg:col-span-7 space-y-10">
            {STORIES.map((s, idx) => (
              <motion.figure
                key={s.author}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: idx * 0.1 }}
                data-testid={`story-${idx}`}
                className="border-t border-[#FAF9F6]/15 pt-8 first:border-t-0 first:pt-0"
              >
                <Quote className="text-[#B0895C]" size={28} strokeWidth={1.2} />
                <blockquote className="font-serif-display text-2xl lg:text-3xl mt-4 leading-snug text-[#FAF9F6]">
                  “{s.quote}”
                </blockquote>
                <figcaption className="mt-5 text-sm text-[#FAF9F6]/70">
                  <span className="text-[#FAF9F6]">{s.author}</span> ·{" "}
                  {s.role}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-[#FAF9F6]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
          <div className="divider-line" />
          <div className="eyebrow mt-10">A quiet invitation</div>
          <h2 className="font-serif-display mt-6 text-4xl lg:text-6xl leading-tight text-[#2A1F1A]">
            Let&apos;s host your home,
            <br />
            <span className="italic text-[#C03028]">together.</span>
          </h2>
          <p className="mt-6 text-[#6E6E70] max-w-xl mx-auto leading-relaxed">
            Tell us about your property in a few quiet steps. We&apos;ll be in touch
            within two working days.
          </p>
          <Link
            to="/onboard"
            data-testid="closing-cta"
            className="btn-primary mt-10 inline-flex items-center gap-2 px-8 py-4 text-sm tracking-wide rounded-none"
          >
            Start your application
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}

function DisplayTierLink({ tier }) {
  // On the landing page tier cards are read-only — clicking the CTA jumps to the onboard flow.
  return (
    <div className="h-full">
      <TierCard
        tier={tier}
        selected={false}
        onSelect={() => {
          window.location.href = `/onboard?tier=${tier.id}`;
        }}
        testIdPrefix="landing-tier"
      />
    </div>
  );
}
